import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Minimal persistent media bar pinned to the bottom of every page.
 * Designed to be unobtrusive — 28px tall, tiny text, tiny controls.
 * Hidden entirely when there are no tracks.
 */
export default function MusicPlayer() {
  const audioRef = useRef(null);
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.10);  // start at 10%
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const autoStartedRef = useRef(false);
  const randomizedRef = useRef(false);
  // When set to true, the next `onLoadedMetadata` fires play() on the new
  // track. Used by prev/next/onEnded to avoid racing with the browser's
  // src-change reload lifecycle.
  const wantsPlayRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/tracks");
      setTracks(res.data.tracks || []);
    } catch {
      // optional endpoint
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Pick a random starting track each time the site loads. Runs once, after
  // the tracks list is first populated, so manual prev/next still wins.
  useEffect(() => {
    if (randomizedRef.current) return;
    if (tracks.length <= 1) return;
    randomizedRef.current = true;
    setIdx(Math.floor(Math.random() * tracks.length));
  }, [tracks]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  /**
   * Try to autoplay the first track as soon as it's loaded. Modern browsers
   * block audible autoplay unless the user has interacted with the page; we
   * try un-muted first, and if the call rejects we fall back to starting it
   * on the next user interaction (any click, key, scroll, or touch).
   */
  useEffect(() => {
    if (autoStartedRef.current) return;
    if (!tracks.length || !audioRef.current) return;
    autoStartedRef.current = true;

    const a = audioRef.current;
    // Defensively re-apply the user-visible volume right before play(). Some
    // browsers reset audio.volume = 1 when src changes (we change src after
    // randomising idx), so the React state and the DOM can drift for one tick.
    a.volume = volume;
    a.muted = muted;
    const tryPlay = async () => {
      try {
        await a.play();
        setPlaying(true);
      } catch {
        // Autoplay was blocked. Wait for any user gesture, then retry.
        const onGesture = () => {
          a.volume = volume;
          a.muted = muted;
          a.play().then(() => setPlaying(true)).catch(() => {});
          window.removeEventListener("pointerdown", onGesture);
          window.removeEventListener("keydown", onGesture);
          window.removeEventListener("scroll", onGesture);
          window.removeEventListener("touchstart", onGesture);
        };
        window.addEventListener("pointerdown", onGesture, { once: false, passive: true });
        window.addEventListener("keydown", onGesture, { once: false });
        window.addEventListener("scroll", onGesture, { once: false, passive: true });
        window.addEventListener("touchstart", onGesture, { once: false, passive: true });
      }
    };
    tryPlay();
  }, [tracks]);

  const current = tracks[idx] || null;

  const next = () => {
    if (tracks.length === 0) return;
    // Preserve current playback intent so onLoadedMetadata resumes the new src.
    wantsPlayRef.current = playing || wantsPlayRef.current;
    setIdx((i) => (i + 1) % tracks.length);
    setProgress(0);
  };

  const prev = () => {
    if (tracks.length === 0) return;
    const a = audioRef.current;
    if (a && a.currentTime > 3) { a.currentTime = 0; return; }
    wantsPlayRef.current = playing || wantsPlayRef.current;
    setIdx((i) => (i - 1 + tracks.length) % tracks.length);
    setProgress(0);
  };

  const toggle = async () => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) {
      try { await a.play(); setPlaying(true); }
      catch { setPlaying(false); }
    } else {
      a.pause(); setPlaying(false);
    }
  };

  const onEnded = () => {
    if (tracks.length > 1) {
      // The user was clearly listening — force the next track to auto-start
      // regardless of whether the browser reports us as paused mid-transition.
      wantsPlayRef.current = true;
      next();
    } else {
      setPlaying(false);
      setProgress(0);
    }
  };

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setProgress(a.currentTime / a.duration);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * a.duration;
    setProgress(ratio);
  };

  if (!tracks.length || !current) return null;

  return (
    <>
      {/* Spacer so fixed-bottom bar doesn't cover footer content */}
      <div aria-hidden style={{ height: 32 }} />
      <div
        data-testid="music-player"
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#0D110C]/95 border-t border-[#283524] backdrop-blur-md font-mono-ui"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Thin seek bar at the top edge */}
        <div
          className="h-[2px] w-full bg-[#161D14] cursor-pointer"
          onClick={seek}
          role="slider"
          aria-label="Seek"
          data-testid="music-seek"
        >
          <div
            className="h-full bg-[#C0A875]"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        <div className="flex items-center gap-3 px-3 sm:px-4 h-7 text-[10px] uppercase tracking-[0.15em]">
          {/* Track meta — single line, tight */}
          <div className="min-w-0 flex-1 truncate text-[#E2E4DF]" data-testid="music-title">
            <span className="text-[#C0A875]">{current.title}</span>
            {current.artist && <span className="text-[#5C6B51]"> · {current.artist}</span>}
          </div>

          {/* Tiny transport */}
          <button
            type="button"
            data-testid="music-prev"
            onClick={prev}
            disabled={tracks.length < 2}
            className="text-[#8D9A87] hover:text-[#E2E4DF] disabled:opacity-30 p-0.5"
            aria-label="Previous track"
          >
            <SkipBack size={11} />
          </button>
          <button
            type="button"
            data-testid="music-toggle"
            onClick={toggle}
            className="text-[#C0A875] hover:text-[#E2E4DF] p-0.5"
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause size={12} /> : <Play size={12} />}
          </button>
          <button
            type="button"
            data-testid="music-next"
            onClick={next}
            disabled={tracks.length < 2}
            className="text-[#8D9A87] hover:text-[#E2E4DF] disabled:opacity-30 p-0.5"
            aria-label="Next track"
          >
            <SkipForward size={11} />
          </button>

          {/* Volume — hidden on tiny screens */}
          <div className="hidden sm:flex items-center gap-1.5 ml-1">
            <button
              type="button"
              data-testid="music-mute"
              onClick={() => setMuted((v) => !v)}
              className="text-[#8D9A87] hover:text-[#E2E4DF] p-0.5"
              aria-label={muted ? "Unmute" : "Mute"}
            >
              {muted || volume === 0 ? <VolumeX size={11} /> : <Volume2 size={11} />}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={(e) => {
                const v = Number(e.target.value);
                setVolume(v);
                if (v > 0 && muted) setMuted(false);
              }}
              data-testid="music-volume"
              className="w-16 h-1 accent-[#C0A875]"
              aria-label="Volume"
            />
          </div>
        </div>

        <audio
          ref={audioRef}
          src={current.url}
          onLoadedMetadata={() => {
            const a = audioRef.current;
            if (!a) return;
            a.volume = muted ? 0 : volume;
            a.muted = muted;
            // If a prev/next/onEnded triggered while we were mid-transition,
            // finish the job now that the new source is ready.
            if (wantsPlayRef.current) {
              wantsPlayRef.current = false;
              a.play().then(() => setPlaying(true)).catch(() => {
                // Autoplay blocked — stay paused, the user will hit play.
                setPlaying(false);
              });
            }
          }}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          preload="metadata"
        />
      </div>
    </>
  );
}
